import { useCallback, useEffect, useRef, useState } from 'react';
import { getSlaExportStatus } from './exportSlaSummary';
import {
  triggerSlaCategoryExport,
  type SlaCategoryExportParams,
} from './exportSlaCategorySummary';

const POLL_INTERVAL_MS = 2_000;

interface UseSlaCategoryExportReturn {
  isExporting: boolean;
  error: string | null;
  startExport: (params: SlaCategoryExportParams) => Promise<void>;
}

export function useSlaCategoryExport(): UseSlaCategoryExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startExport = useCallback(
    async (params: SlaCategoryExportParams) => {
      if (isExporting) return;

      setIsExporting(true);
      setError(null);
      stopPolling();

      try {
        const { jobId } = await triggerSlaCategoryExport(params);

        pollingRef.current = setInterval(async () => {
          try {
            const status = await getSlaExportStatus(jobId);

            if (!mountedRef.current) {
              stopPolling();
              return;
            }

            if (status.status === 'completed' && status.signedUrl) {
              stopPolling();
              setIsExporting(false);
              window.open(status.signedUrl, '_blank');
            } else if (status.status === 'failed') {
              stopPolling();
              setIsExporting(false);
              setError(status.error ?? 'Export failed');
            }
          } catch (pollError) {
            stopPolling();
            if (mountedRef.current) {
              setIsExporting(false);
              setError(
                pollError instanceof Error
                  ? pollError.message
                  : 'Failed to check export status'
              );
            }
          }
        }, POLL_INTERVAL_MS);
      } catch (triggerError) {
        if (mountedRef.current) {
          setIsExporting(false);
          setError(
            triggerError instanceof Error
              ? triggerError.message
              : 'Failed to start category export'
          );
        }
      }
    },
    [isExporting, stopPolling]
  );

  return { isExporting, error, startExport };
}
